// components/LoadingComponent.tsx - Reusable Loading Component

import React from 'react';

interface LoadingComponentProps {
  message?: string;
  size?: 'small' | 'medium' | 'large';
  fullScreen?: boolean;
  className?: string;
}

export const LoadingComponent: React.FC<LoadingComponentProps> = ({
  message = 'Loading...',
  size = 'medium',
  fullScreen = false,
  className = ''
}) => {
  const sizeClasses = {
    small: { spinner: '16px', container: '20px' },
    medium: { spinner: '40px', container: '40px' },
    large: { spinner: '60px', container: '60px' }
  };

  const spinnerSize = sizeClasses[size];

  const containerStyle = fullScreen ? {
    backgroundColor: '#f8fafc',
    minHeight: '100vh',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  } : {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px'
  };

  const innerContainerStyle = fullScreen ? {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '40px',
    textAlign: 'center' as const,
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  } : {
    textAlign: 'center' as const
  };

  return (
    <div style={containerStyle} className={className}>
      <div style={innerContainerStyle}>
        <div
          className="spinner"
          style={{
            width: spinnerSize.spinner,
            height: spinnerSize.spinner,
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}
        />
        <p style={{ color: '#64748b', margin: 0, fontSize: size === 'small' ? '14px' : '16px' }}>
          {message}
        </p>
      </div>
    </div>
  );
};

// Loading overlay for forms
export const LoadingOverlay: React.FC<{ message?: string }> = ({ 
  message = 'Processing...' 
}) => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '32px',
      textAlign: 'center',
      minWidth: '200px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
    }}>
      <div
        className="spinner"
        style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }}
      />
      <p style={{ color: '#64748b', margin: 0, fontWeight: 500 }}>
        {message}
      </p>
    </div>
  </div>
);

// Inline loading for smaller components
export const InlineLoading: React.FC<{ message?: string }> = ({ 
  message = 'Loading...' 
}) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    color: '#64748b'
  }}>
    <div
      className="spinner"
      style={{
        width: '16px',
        height: '16px',
        border: '2px solid #e5e7eb',
        borderTop: '2px solid #3b82f6',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}
    />
    <span style={{ fontSize: '14px' }}>{message}</span>
  </div>
);

// Button loading state
export const ButtonLoading: React.FC = () => (
  <div
    className="spinner"
    style={{
      width: '16px',
      height: '16px',
      border: '2px solid transparent',
      borderTop: '2px solid currentColor',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      display: 'inline-block',
      marginRight: '8px'
    }}
  />
);